import { useState, useEffect, useCallback } from 'react'
import { Header, FuelTypeToggle, SettingsModal, UkStatsModal, OfflineBanner, LocationSearch } from './components'
import { HybridPage } from './pages'
import { useSettings } from './hooks'

/** Background refresh interval in milliseconds (5 minutes) */
const BACKGROUND_REFRESH_INTERVAL = 5 * 60 * 1000

/**
 * Main app component with header navigation
 * Hybrid view shows map with draggable station list
 */
export default function App() {
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [statsOpen, setStatsOpen] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [searchPostcode, setSearchPostcode] = useState<string | null>(null)
  const [searchCoords, setSearchCoords] = useState<{ lat: number; lng: number } | null>(null)
  const { settings, updateSettings } = useSettings()

  // Background refresh
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTrigger((t) => t + 1)
    }, BACKGROUND_REFRESH_INTERVAL)

    return () => clearInterval(interval)
  }, [])

  const handleSelectLocation = useCallback((lat: number, lng: number) => {
    setSearchCoords({ lat, lng })
    setSearchPostcode(null) // Clear postcode search
  }, [])

  const handleSearchPostcode = useCallback((postcode: string) => {
    setSearchPostcode(postcode)
    setSearchCoords(null) // Clear coord search
  }, [])

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Offline indicator */}
      <OfflineBanner />

      {/* Top header with logo and settings */}
      <Header
        onSettingsClick={() => setSettingsOpen(true)}
        onStatsClick={() => setStatsOpen(true)}
      />

      {/* Fixed controls under header */}
      <div className="fixed top-14 left-0 right-0 z-40">
        {/* Location search row */}
        <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
          <div className="px-4 py-2 max-w-2xl mx-auto">
            <LocationSearch
              onSelectLocation={handleSelectLocation}
              onSearchPostcode={handleSearchPostcode}
            />
          </div>
        </div>

        {/* Fuel type toggle row */}
        <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
          <div className="px-4 py-2 max-w-2xl mx-auto">
            <FuelTypeToggle
              value={settings.fuelType}
              onChange={(fuelType) => updateSettings({ fuelType })}
            />
          </div>
        </div>
      </div>

      {/* Page content with padding for fixed header + controls (56 + 52 + 52 = 160px) */}
      <div className="pt-[160px]">
        <HybridPage
          fuelType={settings.fuelType}
          sortBy={settings.sortBy}
          onSortChange={(sortBy) => updateSettings({ sortBy })}
          typicalSpendPounds={settings.typicalSpendPounds}
          refreshTrigger={refreshTrigger}
          searchPostcode={searchPostcode}
          searchCoords={searchCoords}
        />
      </div>

      {/* Settings modal */}
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        updateSettings={updateSettings}
      />

      {/* UK Stats modal */}
      <UkStatsModal
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        fuelType={settings.fuelType}
        onNavigateToStation={handleSelectLocation}
      />
    </div>
  )
}
